import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Diamond,
  Download,
  Eraser,
  FilePlus,
  FileX,
  LayoutTemplate,
  Minus,
  MousePointer2,
  MoveHorizontal,
  MoveRight,
  PaintBucket,
  Pencil,
  RectangleHorizontal,
  Square,
  Trash2,
  Triangle,
  Type,
  Undo2,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  BACKGROUND,
  FILLS,
  INKS,
  LABEL_SIZE,
  PEN_WIDTH,
  TEXT_SIZE,
  WHITEBOARD_HEIGHT,
  WHITEBOARD_WIDTH,
  byId,
  cloneItems,
  drawItem,
  drawSelection,
  drawStroke,
  dragHandle,
  handleAt,
  handlesFor,
  hitTest,
  labelBox,
  lineStep,
  measureLines,
  nextId,
  pieLabelAnchor,
  pieSliceAt,
  shapeAt,
  wrapText,
  alignedLeft,
  PIE_LABEL_WIDTH,
  type Align,
  type ArrowEnds,
  type Connector,
  type Item,
  type Point,
  type Shape,
  type ShapeKind,
  type Stroke,
} from "./whiteboard-items"
import { TEMPLATE_GROUPS, instantiate, type Template } from "./whiteboard-templates"
import { downloadNotes } from "./whiteboard-export"

// The host's whiteboard: a display list of strokes, text, shapes, connectors
// and charts, repainted onto a plain canvas. The call hook captures it with
// canvas.captureStream() and sends it to the client as a second video track
// (same path as screen sharing), so the board exists only in this browser tab
// — nothing is uploaded or persisted anywhere, and the client receives pixels
// rather than any description of what is on it. The one way out is
// "Download": a PDF of the pages, built in the browser and saved to the
// therapist's own device (see whiteboard-export.ts).
//
// The board has pages. Only the current page is painted onto the captured
// canvas, so turning a page is what the client sees; the others sit in
// memory as display lists, each with its own undo history.
//
// Drawing is one-way by design: the therapist draws, the client watches. A
// shared board would need a data channel and a synchronised document, which
// is a different feature with a different compliance story.
//
// The canvas has a fixed logical size; CSS scales it to fit the call area and
// pointer coordinates are mapped back through the bounding rect.

type Tool = "select" | "pen" | "eraser" | "text" | "connector" | ShapeKind

// What the pointer is currently doing, between pointerdown and pointerup.
type Drag =
  | { mode: "draw"; stroke: Stroke }
  | { mode: "erase" }
  | { mode: "shape"; id: string; origin: Point }
  | { mode: "connector"; id: string }
  | { mode: "move"; id: string; last: Point }
  | { mode: "handle"; id: string; handle: string }

// The text being typed right now. `target` says where it will land: a brand
// new text block, an existing item's label, or one slice of a pie chart.
type Draft = {
  target: { kind: "new" } | { kind: "item"; id: string } | { kind: "slice"; id: string; index: number }
  value: string
  x: number
  // Top edge of the text, or its vertical centre when `vcenter` is set —
  // which is how a label inside a shape stays centred as it grows.
  y: number
  vcenter: boolean
  // 0 means "no wrapping": the box grows with the longest line.
  width: number
  align: Align
  size: number
  color: string
}

const SHAPE_TOOLS: { kind: ShapeKind; label: string; icon: typeof Square }[] = [
  { kind: "round", label: "Rounded box", icon: RectangleHorizontal },
  { kind: "rect", label: "Box", icon: Square },
  { kind: "ellipse", label: "Ellipse", icon: Circle },
  { kind: "diamond", label: "Diamond", icon: Diamond },
  { kind: "triangle", label: "Triangle", icon: Triangle },
]

const ARROW_STYLES: { arrow: ArrowEnds; label: string; icon: typeof Square }[] = [
  { arrow: "end", label: "Arrow", icon: MoveRight },
  { arrow: "both", label: "Double arrow", icon: MoveHorizontal },
  { arrow: "none", label: "Plain line", icon: Minus },
]

const DEFAULT_SHAPE = { w: 320, h: 130 }
const DRAW_TOOLS: Tool[] = ["pen", "eraser", "text", "connector"]

// A page that is not currently on the board: its items and its undo stack,
// parked while another page is live.
type Page = { items: Item[]; history: Item[][] }

function isShapeTool(tool: Tool): tool is ShapeKind {
  return !DRAW_TOOLS.includes(tool) && tool !== "select"
}

type Props = {
  // Called once with the mounted canvas so the call hook can capture it.
  onReady: (canvas: HTMLCanvasElement) => void
  onClose: () => void
}

export function Whiteboard({ onReady, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Selection outlines and drag handles are drawn on a second canvas laid
  // over the first. Only the board canvas is captured into the video track,
  // so the client sees the diagram without the therapist's editing chrome.
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemsRef = useRef<Item[]>([])
  const historyRef = useRef<Item[][]>([])
  // Every page but the live one. The live page's items and history are the
  // two refs above; `storePage` parks them here before another is loaded.
  const pagesRef = useRef<Page[]>([{ items: [], history: [] }])
  const pageRef = useRef(0)
  const dragRef = useRef<Drag | null>(null)
  const draftRef = useRef<Draft | null>(null)
  const selectedRef = useRef<string | null>(null)

  const [ink, setInk] = useState(INKS[0].value)
  const [fill, setFill] = useState<string | null>(FILLS[3].value)
  const [tool, setTool] = useState<Tool>("pen")
  const [shapeKind, setShapeKind] = useState<ShapeKind>("round")
  const [arrowStyle, setArrowStyle] = useState<ArrowEnds>("end")
  const [count, setCount] = useState(0)
  const [canUndo, setCanUndo] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [downloading, setDownloading] = useState(false)
  // On-screen geometry of the canvas, relative to its container, so the
  // textarea can be laid over the exact spot the text will be drawn.
  const [box, setBox] = useState({ left: 0, top: 0, width: 0, height: 0 })

  const ctx = () => canvasRef.current?.getContext("2d") ?? null
  const find = (id: string | null) => (id ? (itemsRef.current.find((item) => item.id === id) ?? null) : null)

  // Repaints everything, including whatever is being typed right now — the
  // draft is drawn on the canvas as it is typed so the client sees the words
  // appear rather than waiting for the box to be committed.
  const repaint = useCallback(() => {
    const c = ctx()
    if (!c) return
    c.fillStyle = BACKGROUND
    c.fillRect(0, 0, WHITEBOARD_WIDTH, WHITEBOARD_HEIGHT)
    const items = itemsRef.current
    const lookup = byId(items)
    const d = draftRef.current
    const edit =
      d && d.target.kind !== "new"
        ? { id: d.target.id, value: d.value, sliceIndex: d.target.kind === "slice" ? d.target.index : undefined }
        : undefined
    for (const item of items) drawItem(c, item, lookup, edit)
    if (d && d.target.kind === "new" && d.value !== "") {
      drawItem(
        c,
        { kind: "text", id: "draft", color: d.color, size: d.size, x: d.x, y: d.y, align: d.align, text: d.value },
        lookup,
      )
    }
    const overlay = overlayRef.current?.getContext("2d")
    if (overlay) {
      overlay.clearRect(0, 0, WHITEBOARD_WIDTH, WHITEBOARD_HEIGHT)
      const selected = find(selectedRef.current)
      if (selected) drawSelection(overlay, selected, lookup)
    }
  }, [])

  // Pushes the current board onto the undo stack. Called before every change,
  // so undo restores whole-board state rather than only removing the last
  // thing drawn — moves and resizes have to be undoable too.
  const snapshot = useCallback(() => {
    historyRef.current.push(cloneItems(itemsRef.current))
    if (historyRef.current.length > 50) historyRef.current.shift()
    setCanUndo(true)
  }, [])

  const sync = useCallback(() => {
    setCount(itemsRef.current.length)
    repaint()
  }, [repaint])

  const select = useCallback(
    (id: string | null) => {
      selectedRef.current = id
      setSelectedId(id)
    },
    [],
  )

  useEffect(() => {
    const canvas = canvasRef.current
    const overlay = overlayRef.current
    if (!canvas || !overlay) return
    canvas.width = WHITEBOARD_WIDTH
    canvas.height = WHITEBOARD_HEIGHT
    overlay.width = WHITEBOARD_WIDTH
    overlay.height = WHITEBOARD_HEIGHT
    repaint()
    onReady(canvas)
  }, [onReady, repaint])

  // Track where the scaled canvas actually sits so the text overlay follows
  // it through window resizes and layout changes.
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const measure = () => {
      const c = canvas.getBoundingClientRect()
      const p = container.getBoundingClientRect()
      setBox({ left: c.left - p.left, top: c.top - p.top, width: c.width, height: c.height })
    }
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(canvas)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const toCanvas = (e: React.PointerEvent | React.MouseEvent): Point => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * WHITEBOARD_WIDTH,
      y: ((e.clientY - rect.top) / rect.height) * WHITEBOARD_HEIGHT,
    }
  }

  // ---- typing -----------------------------------------------------------------

  const openDraft = useCallback(
    (next: Draft) => {
      draftRef.current = next
      setDraft(next)
      repaint()
    },
    [repaint],
  )

  // Keeps a finished text box; an empty new one is dropped, and clearing an
  // existing text block deletes it. Safe to call twice — clicking the board
  // both blurs the textarea and starts the next box.
  const commitDraft = useCallback(() => {
    const d = draftRef.current
    draftRef.current = null
    setDraft(null)
    if (!d) return
    if (d.target.kind === "new") {
      if (d.value.trim() !== "") {
        snapshot()
        itemsRef.current.push({
          kind: "text",
          id: nextId(),
          color: d.color,
          size: d.size,
          x: d.x,
          y: d.y,
          align: d.align,
          text: d.value,
        })
      }
    } else {
      const item = find(d.target.id)
      if (item) {
        const current =
          d.target.kind === "slice"
            ? item.kind === "pie"
              ? item.slices[d.target.index].text
              : ""
            : "text" in item
              ? item.text
              : ""
        if (current !== d.value) {
          snapshot()
          if (d.target.kind === "slice" && item.kind === "pie") {
            item.slices[d.target.index].text = d.value
          } else if (item.kind === "text") {
            if (d.value.trim() === "") {
              itemsRef.current = itemsRef.current.filter((other) => other.id !== item.id)
              if (selectedRef.current === item.id) select(null)
            } else {
              item.text = d.value
            }
          } else if (item.kind === "shape") {
            item.text = d.value
          }
        }
      }
    }
    sync()
  }, [select, snapshot, sync])

  const cancelDraft = useCallback(() => {
    draftRef.current = null
    setDraft(null)
    repaint()
  }, [repaint])

  const startTextDraft = useCallback(
    (point: Point) => {
      commitDraft()
      openDraft({
        target: { kind: "new" },
        value: "",
        x: point.x,
        // Nudge the box up by half a line so the click lands roughly at the
        // middle of the first line, the way a text cursor behaves elsewhere.
        y: Math.max(0, point.y - lineStep(TEXT_SIZE) / 2),
        vcenter: false,
        width: 0,
        align: "left",
        size: TEXT_SIZE,
        color: ink,
      })
    },
    [commitDraft, ink, openDraft],
  )

  // Opens the label editor for whatever is under the point — a shape's
  // centred label, an existing text block, or one slice of a pie chart.
  const editItemAt = useCallback(
    (point: Point) => {
      const c = ctx()
      if (!c) return false
      const item = hitTest(c, itemsRef.current, point)
      if (!item) return false
      commitDraft()
      if (item.kind === "shape") {
        const { width, cy } = labelBox(item)
        openDraft({
          target: { kind: "item", id: item.id },
          value: item.text,
          x: item.x + item.w / 2,
          y: cy,
          vcenter: true,
          width,
          align: "center",
          size: item.size,
          color: item.color,
        })
      } else if (item.kind === "text") {
        openDraft({
          target: { kind: "item", id: item.id },
          value: item.text,
          x: item.x,
          y: item.y,
          vcenter: false,
          width: 0,
          align: item.align,
          size: item.size,
          color: item.color,
        })
      } else if (item.kind === "pie") {
        const index = pieSliceAt(item, point)
        if (index === null) return false
        const anchor = pieLabelAnchor(item, index)
        openDraft({
          target: { kind: "slice", id: item.id, index },
          value: item.slices[index].text,
          x: anchor.x,
          y: anchor.y,
          vcenter: true,
          width: PIE_LABEL_WIDTH,
          align: anchor.align,
          size: anchor.size,
          color: item.color,
        })
      } else {
        return false
      }
      select(item.id)
      return true
    },
    [commitDraft, openDraft, select],
  )

  // ---- pointer ----------------------------------------------------------------

  const eraseAt = (point: Point) => {
    const c = ctx()
    if (!c) return
    const item = hitTest(c, itemsRef.current, point)
    if (!item) return
    itemsRef.current = itemsRef.current.filter((other) => other.id !== item.id)
    // A shape taking its bound connectors with it: an arrow left dangling in
    // mid-air after its box is gone is never what was meant.
    if (item.kind === "shape") {
      itemsRef.current = itemsRef.current.filter(
        (other) => !(other.kind === "connector" && (other.fromId === item.id || other.toId === item.id)),
      )
    }
    if (selectedRef.current === item.id) select(null)
    sync()
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === "mouse") return
    const point = toCanvas(e)
    const c = ctx()
    if (!c) return

    if (tool === "text") {
      // Cancels the click's default focus change, which would otherwise blur
      // (and so immediately commit) the text box we are about to open.
      e.preventDefault()
      startTextDraft(point)
      return
    }
    commitDraft()

    if (tool === "select") {
      const selected = find(selectedRef.current)
      if (selected) {
        const handle = handleAt(handlesFor(c, selected, byId(itemsRef.current)), point)
        if (handle) {
          snapshot()
          dragRef.current = { mode: "handle", id: selected.id, handle: handle.id }
          capture(e)
          return
        }
      }
      const hit = hitTest(c, itemsRef.current, point)
      select(hit?.id ?? null)
      if (hit) {
        snapshot()
        dragRef.current = { mode: "move", id: hit.id, last: point }
        capture(e)
      }
      repaint()
      return
    }

    select(null)
    capture(e)

    if (tool === "pen") {
      snapshot()
      const stroke: Stroke = { kind: "stroke", id: nextId(), color: ink, width: PEN_WIDTH, points: [point] }
      dragRef.current = { mode: "draw", stroke }
      repaint()
      drawStroke(c, { ...stroke, points: [point, point] })
      return
    }

    if (tool === "eraser") {
      snapshot()
      dragRef.current = { mode: "erase" }
      eraseAt(point)
      return
    }

    if (tool === "connector") {
      snapshot()
      const from = shapeAt(c, itemsRef.current, point)
      const connector: Connector = {
        kind: "connector",
        id: nextId(),
        color: ink,
        from: point,
        to: point,
        fromId: from?.id,
        arrow: arrowStyle,
        bow: 0,
      }
      itemsRef.current.push(connector)
      dragRef.current = { mode: "connector", id: connector.id }
      sync()
      return
    }

    // A shape tool: drag out the new shape from this corner.
    snapshot()
    const shape: Shape = {
      kind: "shape",
      id: nextId(),
      shape: tool,
      x: point.x,
      y: point.y,
      w: 0,
      h: 0,
      color: ink,
      fill,
      size: LABEL_SIZE,
      text: "",
    }
    itemsRef.current.push(shape)
    dragRef.current = { mode: "shape", id: shape.id, origin: point }
    sync()
  }

  const capture = (e: React.PointerEvent) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* pointer already gone — drawing still works, capture is best-effort */
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    const c = ctx()
    if (!drag || !c) return
    const point = toCanvas(e)

    switch (drag.mode) {
      case "draw": {
        const last = drag.stroke.points[drag.stroke.points.length - 1]
        drag.stroke.points.push(point)
        // Freehand draws the new segment only: repainting the whole board on
        // every pointer move would be quadratic in the stroke's own length.
        drawStroke(c, { ...drag.stroke, points: [last, point] })
        return
      }
      case "erase":
        eraseAt(point)
        return
      case "shape": {
        const shape = find(drag.id)
        if (shape?.kind !== "shape") return
        shape.x = Math.min(drag.origin.x, point.x)
        shape.y = Math.min(drag.origin.y, point.y)
        shape.w = Math.abs(point.x - drag.origin.x)
        shape.h = Math.abs(point.y - drag.origin.y)
        repaint()
        return
      }
      case "connector": {
        const connector = find(drag.id)
        if (connector?.kind !== "connector") return
        connector.to = point
        repaint()
        return
      }
      case "move": {
        const item = find(drag.id)
        if (!item) return
        moveSelection(item, point.x - drag.last.x, point.y - drag.last.y)
        drag.last = point
        repaint()
        return
      }
      case "handle": {
        const item = find(drag.id)
        if (!item) return
        dragHandle(item, drag.handle, point)
        repaint()
      }
    }
  }

  // Moving a shape carries any connector endpoint that is sitting loose on
  // it; bound endpoints follow on their own.
  const moveSelection = (item: Item, dx: number, dy: number) => {
    const move = (target: Item) => {
      if (target.kind === "stroke") {
        for (const p of target.points) {
          p.x += dx
          p.y += dy
        }
      } else if (target.kind === "connector") {
        if (!target.fromId) target.from = { x: target.from.x + dx, y: target.from.y + dy }
        if (!target.toId) target.to = { x: target.to.x + dx, y: target.to.y + dy }
      } else {
        target.x += dx
        target.y += dy
      }
    }
    move(item)
  }

  const endDrag = () => {
    const drag = dragRef.current
    dragRef.current = null
    const c = ctx()
    if (!drag || !c) return

    if (drag.mode === "draw") {
      itemsRef.current.push(drag.stroke)
      sync()
      return
    }
    if (drag.mode === "shape") {
      const shape = find(drag.id)
      if (shape?.kind === "shape") {
        // A tap rather than a drag: drop a default-sized shape centred on it.
        if (shape.w < 30 || shape.h < 30) {
          shape.x = shape.x - DEFAULT_SHAPE.w / 2
          shape.y = shape.y - DEFAULT_SHAPE.h / 2
          shape.w = DEFAULT_SHAPE.w
          shape.h = DEFAULT_SHAPE.h
        }
        select(shape.id)
        setTool("select")
        sync()
        // Straight into typing its label, which is what a new box is for.
        const { width, cy } = labelBox(shape)
        openDraft({
          target: { kind: "item", id: shape.id },
          value: "",
          x: shape.x + shape.w / 2,
          y: cy,
          vcenter: true,
          width,
          align: "center",
          size: shape.size,
          color: shape.color,
        })
      }
      return
    }
    if (drag.mode === "connector") {
      const connector = find(drag.id)
      if (connector?.kind === "connector") {
        const target = shapeAt(c, itemsRef.current, connector.to)
        // Binding to the shape it was dropped on is what makes arrows follow
        // their boxes around the board.
        if (target && target.id !== connector.fromId) connector.toId = target.id
        const tiny = Math.hypot(connector.to.x - connector.from.x, connector.to.y - connector.from.y) < 24
        if (tiny && !connector.toId) {
          itemsRef.current = itemsRef.current.filter((item) => item.id !== connector.id)
        } else {
          select(connector.id)
          setTool("select")
        }
      }
      sync()
      return
    }
    if (drag.mode === "handle") {
      const connector = find(drag.id)
      if (connector?.kind === "connector") {
        const end = drag.handle === "from" ? connector.from : connector.to
        const target = shapeAt(c, itemsRef.current, end)
        if (target) {
          if (drag.handle === "from") connector.fromId = target.id
          else connector.toId = target.id
        }
      }
      sync()
      return
    }
    sync()
  }

  const onDoubleClick = (e: React.MouseEvent) => {
    if (tool === "text") return
    e.preventDefault()
    if (editItemAt(toCanvas(e))) setTool("select")
  }

  // ---- board commands ---------------------------------------------------------

  const deleteSelected = useCallback(() => {
    const item = find(selectedRef.current)
    if (!item) return
    snapshot()
    itemsRef.current = itemsRef.current.filter((other) => other.id !== item.id)
    if (item.kind === "shape") {
      itemsRef.current = itemsRef.current.filter(
        (other) => !(other.kind === "connector" && (other.fromId === item.id || other.toId === item.id)),
      )
    }
    select(null)
    sync()
  }, [select, snapshot, sync])

  const undo = useCallback(() => {
    if (draftRef.current) {
      cancelDraft()
      return
    }
    const previous = historyRef.current.pop()
    if (!previous) return
    itemsRef.current = previous
    setCanUndo(historyRef.current.length > 0)
    select(null)
    sync()
  }, [cancelDraft, select, sync])

  const clear = () => {
    draftRef.current = null
    setDraft(null)
    if (itemsRef.current.length > 0) snapshot()
    itemsRef.current = []
    select(null)
    sync()
  }

  const insertTemplate = (template: Template) => {
    commitDraft()
    snapshot()
    itemsRef.current.push(...instantiate(template))
    select(null)
    setTool("select")
    sync()
  }

  // ---- pages ------------------------------------------------------------------

  // Parks the live page's items and history in the page list.
  const storePage = () => {
    pagesRef.current[pageRef.current] = { items: itemsRef.current, history: historyRef.current }
  }

  // Makes another page the live one. The client sees the new page the moment
  // it is painted, since the captured canvas is the only board there is.
  const showPage = (index: number) => {
    const page = pagesRef.current[index]
    if (!page) return
    pageRef.current = index
    itemsRef.current = page.items
    historyRef.current = page.history
    setPageIndex(index)
    setPageCount(pagesRef.current.length)
    setCanUndo(page.history.length > 0)
    select(null)
    sync()
  }

  const goToPage = (index: number) => {
    if (index < 0 || index >= pagesRef.current.length || index === pageRef.current) return
    commitDraft()
    storePage()
    showPage(index)
  }

  const addPage = () => {
    commitDraft()
    storePage()
    pagesRef.current.push({ items: [], history: [] })
    showPage(pagesRef.current.length - 1)
  }

  // Removes the live page. Each page's undo stack is its own, so a deleted
  // page cannot come back — hence the question when it has anything on it.
  const deletePage = () => {
    if (pagesRef.current.length < 2) return
    commitDraft()
    if (itemsRef.current.length > 0 && !window.confirm("Delete this page and everything on it?")) return
    pagesRef.current.splice(pageRef.current, 1)
    showPage(Math.min(pageRef.current, pagesRef.current.length - 1))
  }

  // Every page, live one included, as the download and the "anything to
  // download?" check see them.
  const allPages = () => {
    storePage()
    return pagesRef.current.map((page) => page.items)
  }

  const download = async () => {
    commitDraft()
    setDownloading(true)
    try {
      await downloadNotes(allPages())
    } finally {
      setDownloading(false)
    }
  }

  // Colour and fill apply to the selection when there is one, and otherwise
  // set what the next thing drawn will use.
  const applyInk = (value: string) => {
    setInk(value)
    if (tool === "eraser") setTool("pen")
    const item = find(selectedRef.current)
    if (!item) return
    snapshot()
    item.color = value
    sync()
  }

  const applyFill = (value: string | null) => {
    setFill(value)
    const item = find(selectedRef.current)
    if (item?.kind === "shape") {
      snapshot()
      item.fill = value
      sync()
    }
  }

  const chooseTool = (next: Tool) => {
    // Leaving the text tool keeps whatever has been typed so far.
    if (next !== "text") commitDraft()
    if (next !== "select") select(null)
    setTool(next)
    repaint()
  }

  // Keyboard shortcuts, scoped to the board: the textarea handles its own
  // keys, so typing a Backspace into a label must not delete the shape.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "TEXTAREA" || target.tagName === "INPUT")) return
      if (e.key === "Escape") {
        select(null)
        repaint()
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (!selectedRef.current) return
        e.preventDefault()
        deleteSelected()
      } else if (e.key.toLowerCase() === "z" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        undo()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [deleteSelected, repaint, select, undo])

  // ---- the typing overlay -----------------------------------------------------

  // Laid out from the same font metrics the canvas uses, so the caret sits
  // exactly where the letters are drawn. The text itself is transparent —
  // the canvas underneath is the visible copy.
  const scale = box.height / WHITEBOARD_HEIGHT
  const layout = useMemo(() => {
    const c = ctx()
    if (!c || !draft) return null
    const lines = draft.width > 0 ? wrapText(c, draft.value, draft.size, draft.width) : draft.value.split("\n")
    const height = Math.max(1, lines.length) * lineStep(draft.size)
    const measured = measureLines(c, lines, draft.size).w
    const width =
      draft.width > 0
        ? draft.width
        : Math.min(measured + draft.size * 1.5, Math.max(draft.size * 4, WHITEBOARD_WIDTH - draft.x))
    return {
      left: alignedLeft(draft.x, width, draft.align),
      top: draft.vcenter ? draft.y - height / 2 : draft.y,
      width,
      height,
      wrap: draft.width > 0,
    }
    // Recomputed on every keystroke: the box grows and re-centres as it fills.
  }, [draft])

  const cursor =
    tool === "text" ? "cursor-text" : tool === "select" ? "cursor-default" : tool === "eraser" ? "cursor-pointer" : "cursor-crosshair"

  const toolButton = (value: Tool, label: string, Icon: typeof Square, onClick?: () => void) => (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick ?? (() => chooseTool(value))}
      aria-pressed={tool === value}
      aria-label={label}
      className={cn(tool === value && "ring-2 ring-teal")}
    >
      <Icon className="h-4 w-4" />
      <span className="ml-2 hidden lg:inline">{label}</span>
    </Button>
  )

  const activeShape = SHAPE_TOOLS.find((s) => s.kind === shapeKind) ?? SHAPE_TOOLS[0]
  const activeArrow = ARROW_STYLES.find((a) => a.arrow === arrowStyle) ?? ARROW_STYLES[0]
  // Anything on any page is worth downloading. The parked pages are read
  // from the ref: every change to them goes through a page switch, which
  // re-renders, so this stays current.
  const hasNotes =
    count > 0 || pagesRef.current.some((page, i) => i !== pageRef.current && page.items.length > 0)

  return (
    <div className="absolute inset-0 flex flex-col bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline bg-white px-3 py-2">
        <div className="flex items-center gap-1" role="group" aria-label="Pen color">
          {INKS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => applyInk(c.value)}
              aria-label={`${c.name} pen`}
              aria-pressed={tool !== "eraser" && ink === c.value}
              className={cn(
                "h-7 w-7 rounded-full border-2",
                tool !== "eraser" && ink === c.value ? "border-teal" : "border-transparent",
              )}
            >
              <span className="block h-full w-full rounded-full border border-white" style={{ backgroundColor: c.value }} />
            </button>
          ))}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" aria-label="Shape fill">
              <PaintBucket className="h-4 w-4" />
              <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Fill</DropdownMenuLabel>
            {FILLS.map((f) => (
              <DropdownMenuItem key={f.name} onSelect={() => applyFill(f.value)}>
                <span
                  className="h-4 w-4 rounded-sm border border-hairline"
                  style={{ backgroundColor: f.value ?? "transparent" }}
                />
                {f.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {toolButton("select", "Select", MousePointer2)}
        {toolButton("pen", "Pen", Pencil)}

        <div className="flex items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => chooseTool(shapeKind)}
            aria-pressed={isShapeTool(tool)}
            aria-label="Shape tool"
            className={cn("rounded-r-none", isShapeTool(tool) && "ring-2 ring-teal")}
          >
            <activeShape.icon className="h-4 w-4" />
            <span className="ml-2 hidden lg:inline">Shape</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" aria-label="Choose a shape" className="rounded-l-none border-l px-2">
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {SHAPE_TOOLS.map((s) => (
                <DropdownMenuItem
                  key={s.kind}
                  onSelect={() => {
                    setShapeKind(s.kind)
                    chooseTool(s.kind)
                  }}
                >
                  <s.icon className="h-4 w-4" />
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => chooseTool("connector")}
            aria-pressed={tool === "connector"}
            aria-label="Arrow tool"
            className={cn("rounded-r-none", tool === "connector" && "ring-2 ring-teal")}
          >
            <activeArrow.icon className="h-4 w-4" />
            <span className="ml-2 hidden lg:inline">Arrow</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" aria-label="Choose an arrow style" className="rounded-l-none border-l px-2">
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {ARROW_STYLES.map((a) => (
                <DropdownMenuItem
                  key={a.arrow}
                  onSelect={() => {
                    setArrowStyle(a.arrow)
                    chooseTool("connector")
                  }}
                >
                  <a.icon className="h-4 w-4" />
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {toolButton("text", "Text tool", Type, () => chooseTool(tool === "text" ? "pen" : "text"))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" aria-label="Insert a diagram">
              <LayoutTemplate className="h-4 w-4" />
              <span className="ml-2 hidden lg:inline">Diagrams</span>
              <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[70vh] overflow-y-auto">
            {TEMPLATE_GROUPS.map((group, i) => (
              <div key={group.group}>
                {i > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel>{group.group}</DropdownMenuLabel>
                {group.templates.map((template) => (
                  <DropdownMenuItem key={template.key} onSelect={() => insertTemplate(template)}>
                    {template.name}
                  </DropdownMenuItem>
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {toolButton("eraser", "Eraser", Eraser, () => chooseTool(tool === "eraser" ? "pen" : "eraser"))}

        {selectedId && (
          <Button variant="secondary" size="sm" onClick={deleteSelected} aria-label="Delete the selected item">
            <Trash2 className="h-4 w-4" />
            <span className="ml-2 hidden lg:inline">Delete</span>
          </Button>
        )}

        <Button variant="secondary" size="sm" onClick={undo} disabled={!canUndo && !draft} aria-label="Undo">
          <Undo2 className="h-4 w-4" />
          <span className="ml-2 hidden lg:inline">Undo</span>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={clear}
          disabled={count === 0 && !draft}
          aria-label="Clear the whiteboard"
        >
          <Trash2 className="h-4 w-4" />
          <span className="ml-2 hidden lg:inline">Clear</span>
        </Button>

        {tool === "text" && (
          <span className="hidden text-sm text-ink-muted xl:inline">Click the board and type · Esc to cancel</span>
        )}
        {tool === "select" && (
          <span className="hidden text-sm text-ink-muted xl:inline">Drag to move · double-click to label</span>
        )}

        <div className="ml-auto flex items-center gap-1" role="group" aria-label="Pages">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => goToPage(pageIndex - 1)}
            disabled={pageIndex === 0}
            aria-label="Previous page"
            className="px-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[4.5rem] text-center text-sm text-ink-body" aria-live="polite">
            Page {pageIndex + 1} of {pageCount}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => goToPage(pageIndex + 1)}
            disabled={pageIndex >= pageCount - 1}
            aria-label="Next page"
            className="px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={addPage} aria-label="Add a page">
            <FilePlus className="h-4 w-4" />
            <span className="ml-2 hidden lg:inline">Add page</span>
          </Button>
          {pageCount > 1 && (
            <Button variant="secondary" size="sm" onClick={deletePage} aria-label="Delete this page">
              <FileX className="h-4 w-4" />
            </Button>
          )}
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={download}
          disabled={downloading || !hasNotes}
          aria-label="Download the notes as a PDF"
        >
          <Download className="h-4 w-4" />
          <span className="ml-2 hidden lg:inline">Download</span>
        </Button>

        <Button variant="secondary" size="sm" onClick={onClose} aria-label="Close the whiteboard panel">
          <X className="h-4 w-4" />
          <span className="ml-2 hidden sm:inline">Close</span>
        </Button>
      </div>

      {/* Center the canvas at its own 16:10 ratio (canvas is a replaced
          element, so max-w/max-h scale it proportionally). The on-screen box
          then matches the captured bitmap exactly, which keeps pointer
          mapping accurate and shows the host what the client sees. */}
      <div ref={containerRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        <canvas
          ref={canvasRef}
          aria-label="Whiteboard drawing area"
          className={cn("max-h-full max-w-full", cursor)}
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={onDoubleClick}
        />
        <canvas
          ref={overlayRef}
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
        />
        {draft && layout && (
          <textarea
            key={`${draft.target.kind}:${draft.target.kind === "new" ? `${draft.x},${draft.y}` : draft.target.id}`}
            autoFocus
            aria-label="Whiteboard text"
            value={draft.value}
            onChange={(e) => {
              const next = { ...draft, value: e.target.value }
              draftRef.current = next
              setDraft(next)
              repaint()
            }}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault()
                cancelDraft()
              } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                commitDraft()
              }
            }}
            spellCheck={false}
            className="absolute resize-none overflow-hidden border-0 bg-transparent p-0 text-transparent outline-1 outline-dashed outline-teal"
            style={{
              left: box.left + (layout.left / WHITEBOARD_WIDTH) * box.width,
              top: box.top + (layout.top / WHITEBOARD_HEIGHT) * box.height,
              width: layout.width * scale,
              height: layout.height * scale,
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: draft.size * scale,
              lineHeight: 1.3,
              textAlign: draft.align,
              caretColor: draft.color,
              whiteSpace: layout.wrap ? "pre-wrap" : "pre",
            }}
          />
        )}
      </div>
    </div>
  )
}
